import React, { useState } from 'react';
import { useBrandingColors, getButtonInlineStyles, getButtonHoverStyles } from '../../hooks/useBrandingColors';
import {
  companiesManagementService,
  kopToRubSmart,
  STATUS_COLORS,
  type Company,
} from '../../services/companiesManagementService';

interface Props {
  company: Company;
  onClose: () => void;
  onSuccess: () => void;
}

// Пресеты сумм по макету «Пополнение баланса» (₽).
const QUICK_AMOUNTS_RUB = [10_000, 50_000, 100_000];

// ─── Иконки строго по макету ────────────────────────────────────────────────

const CloseIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="shrink-0">
    <path
      d="M16 14.303 21.94 8.363l1.697 1.697L17.697 16l5.94 5.94-1.697 1.697L16 17.697l-5.94 5.94-1.697-1.697L14.303 16l-5.94-5.94 1.697-1.697L16 14.303Z"
      fill="#868C98"
    />
  </svg>
);

// team-line — иконка компании (как в карточке), fill #E16349
const TeamIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" className="shrink-0">
    <path
      d="M20 18a7.5 7.5 0 0 1 7.5 7.5v9h-3v-9a4.5 4.5 0 0 0-4.5-4.5 4.5 4.5 0 0 0-4.5 4.5v9h-3v-9A7.5 7.5 0 0 1 20 18Zm-9.75 4.5c.42 0 .82.05 1.21.14a8.94 8.94 0 0 0-.46 2.86v9h-3v-6.75a3.75 3.75 0 0 1 2.25-5.25Zm19.5 0a3.75 3.75 0 0 1 1.5 7.18V34.5h-3v-9c0-1-.16-1.96-.46-2.86.39-.09.79-.14 1.21-.14ZM10.25 13.5a3.75 3.75 0 1 1 0 7.5 3.75 3.75 0 0 1 0-7.5Zm19.5 0a3.75 3.75 0 1 1 0 7.5 3.75 3.75 0 0 1 0-7.5ZM20 4.5a6 6 0 1 1 0 12 6 6 0 0 1 0-12Zm0 3a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"
      fill="#E16349"
    />
  </svg>
);

// settings-3-fill — шестерёнка возле названия, #989286
const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#989286" className="shrink-0">
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.48.48 0 0 0-.48-.41h-3.84a.48.48 0 0 0-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87a.49.49 0 0 0 .12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.01-1.58ZM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2Z" />
  </svg>
);

// wallet — иконка перед «Баланс компании» (пути из макета, viewBox по их координатам)
const WalletIcon = () => (
  <svg width="14" height="14" viewBox="47 189 14 14" fill="none" className="shrink-0">
    <path
      d="M58.0833 193.083V191.333C58.0833 191.179 58.0219 191.03 57.9125 190.921C57.8031 190.811 57.6547 190.75 57.5 190.75H49.9167C49.6072 190.75 49.3105 190.873 49.0917 191.092C48.8729 191.311 48.75 191.607 48.75 191.917C48.75 192.226 48.8729 192.523 49.0917 192.742C49.3105 192.96 49.6072 193.083 49.9167 193.083H58.6667C58.8214 193.083 58.9697 193.145 59.0791 193.254C59.1885 193.364 59.25 193.512 59.25 193.667V196M59.25 196H57.5C57.1906 196 56.8938 196.123 56.675 196.342C56.4562 196.561 56.3333 196.857 56.3333 197.167C56.3333 197.476 56.4562 197.773 56.675 197.992C56.8938 198.21 57.1906 198.333 57.5 198.333H59.25C59.4047 198.333 59.5531 198.272 59.6625 198.162C59.7719 198.053 59.8333 197.905 59.8333 197.75V196.583C59.8333 196.429 59.7719 196.28 59.6625 196.171C59.5531 196.061 59.4047 196 59.25 196Z"
      stroke="#161922" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"
    />
    <path
      d="M48.75 191.916V200.083C48.75 200.392 48.8729 200.689 49.0917 200.908C49.3105 201.126 49.6072 201.249 49.9167 201.249H58.6667C58.8214 201.249 58.9697 201.188 59.0791 201.078C59.1885 200.969 59.25 200.821 59.25 200.666V198.333"
      stroke="#161922" strokeWidth="1.17" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
);

export const TopUpBalanceModal: React.FC<Props> = ({ company, onClose, onSuccess }) => {
  const colors = useBrandingColors();
  // Храним сырые цифры рублей; в инпуте показываем с разделителями (10 000).
  const [digits, setDigits] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amountRub = digits ? parseInt(digits, 10) : 0;
  const canSubmit = !loading && amountRub > 0;
  const display = digits ? amountRub.toLocaleString('ru-RU') : '';

  const onAmount = (val: string) => {
    setDigits(val.replace(/\D/g, ''));
    setError(null);
  };
  const pickPreset = (rub: number) => {
    setDigits(String(rub));
    setError(null);
  };

  const handleSubmit = async () => {
    if (amountRub <= 0) {
      setError('Введите сумму пополнения');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await companiesManagementService.topUpBalance(company.id!, {
        amountKopecks: amountRub * 100,
        reason: `Пополнение баланса компании ${company.name}`,
      });
      onSuccess();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка пополнения');
    } finally {
      setLoading(false);
    }
  };

  const dotColor = STATUS_COLORS[company.status] ?? '#38C793';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      {/* Контейнер по макету: 540px, padding 24, gap 24, фон #F5F6F1, radius 20 */}
      <div className="w-[540px] max-w-[calc(100vw-32px)] max-h-[90vh] overflow-y-auto bg-[#F5F6F1] rounded-[20px] p-6 flex flex-col gap-6">

        {/* Заголовок: «Пополнение баланса» по центру + × справа */}
        <div className="flex items-start justify-between gap-6">
          <span className="w-8 shrink-0" />
          <h2 className="flex-1 text-center text-[24px] font-medium leading-8 tracking-[-0.015em] text-black">
            Пополнение баланса
          </h2>
          <button type="button" onClick={onClose} aria-label="Закрыть" className="shrink-0">
            <CloseIcon />
          </button>
        </div>

        {/* Шапка компании + карточка баланса */}
        <div className="flex flex-col gap-6">
          {/* Компания */}
          <div className="flex items-center gap-[18px]">
            <div className="relative w-16 h-16 shrink-0">
              <div className="w-16 h-16 bg-white rounded-[12px] flex items-center justify-center">
                <TeamIcon />
              </div>
              <span
                className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border border-black/20 box-border"
                style={{ backgroundColor: dotColor, boxShadow: '0 0 0 3px #F5F6F1' }}
              />
            </div>
            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
              <div className="flex items-center gap-1 min-w-0">
                <span className="text-[24px] font-medium leading-8 tracking-[-0.015em] text-[#161922] truncate">
                  {company.name}
                </span>
                <SettingsIcon />
              </div>
              {company.ownerEmail && (
                <span className="text-[14px] font-medium leading-[22px] tracking-[-0.234px] text-[#989286] truncate">
                  {company.ownerEmail}
                </span>
              )}
            </div>
          </div>

          {/* Карточка баланса */}
          <div className="bg-white border border-[#F4F5F0] rounded-[16px] px-4 py-[14px] flex flex-col gap-2">
            <div className="flex items-center gap-[10px]">
              <span className="flex items-start justify-center w-[26px] h-[26px] bg-[#F4F5F0] rounded-[6px] shrink-0 pt-1.5">
                <WalletIcon />
              </span>
              <span className="text-[14px] font-semibold leading-[21px] tracking-[-0.15px] text-[#161922]">
                Баланс компании
              </span>
            </div>
            <span className="text-[20px] font-bold leading-[30px] tracking-[-0.449px] text-[#161922]">
              {kopToRubSmart(company.balanceKopecks ?? 0)} ₽
            </span>
          </div>
        </div>

        {/* Ввод суммы */}
        <div className="flex flex-col gap-3">
          <span className="text-[16px] font-medium leading-6 tracking-[-0.011em] text-[#0A0D14]">
            Введите или выберите сумму
          </span>

          <div className="flex flex-col gap-3">
            {/* Инпут суммы (фокус — оранжевая рамка, ₽ справа) */}
            <div
              className={`flex items-center gap-2 h-[52px] bg-white rounded-[10px] pl-[14px] pr-3 border transition-colors shadow-[0px_1px_2px_rgba(228,229,231,0.24)] ${
                error ? 'border-[#DF1C41]' : 'border-[#E2E4E9] focus-within:border-[#E16349]'
              }`}
            >
              <input
                type="text"
                inputMode="numeric"
                placeholder="Сумма пополнения"
                value={display}
                onChange={e => onAmount(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && canSubmit && handleSubmit()}
                className="flex-1 min-w-0 bg-transparent text-[16px] leading-5 tracking-[-0.006em] text-[#0A0D14] placeholder:text-[#0A0D14] outline-none"
              />
              <span className="text-[16px] leading-[22px] tracking-[-0.234px] text-[#99A1AF] shrink-0">₽</span>
            </div>

            {/* Пресеты сумм */}
            <div className="flex items-center gap-2">
              {QUICK_AMOUNTS_RUB.map(rub => {
                const active = digits === String(rub);
                return (
                  <button
                    key={rub}
                    type="button"
                    onClick={() => pickPreset(rub)}
                    className={`flex-1 flex items-center justify-center h-10 rounded-[10px] border text-[14px] font-medium leading-5 tracking-[-0.006em] text-[#0A0D14] shadow-[0px_1px_2px_rgba(55,93,251,0.08)] transition-colors ${
                      active ? 'border-[#E16349] bg-[rgba(225,99,73,0.06)]' : 'border-[#E2E4E9] bg-white hover:border-[#E16349]'
                    }`}
                  >
                    {rub.toLocaleString('ru-RU')} ₽
                  </button>
                );
              })}
            </div>
          </div>

          {error && <p className="text-[14px] text-[#DF1C41]" role="alert">{error}</p>}
        </div>

        {/* Кнопки: Отмена / Пополнить */}
        <div className="flex justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-12 rounded-[12px] bg-[#E9EAE4] text-[#7D776C] text-[15px] font-semibold leading-7 tracking-[0.01em] hover:bg-[#e0e1da] transition-colors"
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex-1 h-12 rounded-[12px] text-white text-[15px] font-semibold leading-7 tracking-[0.01em] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            style={getButtonInlineStyles(colors, 'primary')}
            onMouseEnter={e => { if (canSubmit) Object.assign(e.currentTarget.style, getButtonHoverStyles(colors, 'primary')); }}
            onMouseLeave={e => { if (canSubmit) Object.assign(e.currentTarget.style, getButtonInlineStyles(colors, 'primary')); }}
          >
            {loading ? 'Пополнение...' : 'Пополнить'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopUpBalanceModal;
